import { useDetectAdBlock } from "adblock-detect-react";
import classNames from "classnames";
import type {
  GetServerSideProps,
  InferGetServerSidePropsType,
  NextPage,
} from "next";
import Head from "next/head";
import { useState } from "react";
import { v4 } from "uuid";
import Advertisement from "../../../components/Advertisement";
import Container from "../../../components/Container";
import PrimaryGrid from "../../../components/Grid/PrimaryGrid";
import { Media } from "../../../components/Media";
import Navigation from "../../../components/Navigation";
import ArticleFeedDesktop from "../../../components/pages/article/ArticleFeedDesktop";
import ArticleFeedMobile from "../../../components/pages/article/ArticleFeedMobile";
import StickySidebar from "../../../components/StickySidebar";
import AdsIds, {
  AdsIdsMobileCollection,
  AdsIdsPanoramaCollection,
} from "../../../data/ads";
import useCanonicalUrl from "../../../hooks/useCanonicalUrl";
import { listByIds } from "../../../services/api/ads";
import {
  getCampaigns,
  getCampaignsByIds,
} from "../../../services/api/campaigns";
import { getGames } from "../../../services/api/games";
import {
  getArticle,
  getArticleTags,
  getNewsList,
} from "../../../services/api/news";
import { API } from "../../../services/api/types";
import { AdsContext } from "../../../utils/AdsContext";
import {
  getCampaignClubId,
  getClubCampaignId,
  getRandomCampaign,
} from "../../../utils/campaigns";
import { toMysqlDateString } from "../../../utils/date";
import { DeviceTypeProvider } from "../../../utils/deviceTypeContext";
import { createServerSidePropsError } from "../../../utils/errorHandling/createServerSidePropsError";
import { logError } from "../../../utils/errorHandling/logError";
import { DeviceType, getDeviceType } from "../../../utils/getDeviceType";
import slugify from "../../../utils/slugify";
import { getGamesLeagueByCategoryId } from "../../../utils/sponsoredOdds";

const last = (arr: any[]) => arr[arr.length - 1];

export type ArticleWithTags = {
  article: API.Article;
  featuredNews: API.ArticleTag[];
  tags: API.ArticleTag[];
  games: API.Game[];
  campaign?: API.Campaign;
};

const getArticleWithTags = async (id: number, _article?: API.Article) => {
  const article = _article || (await getArticle(id)()).data;

  const articleTags = (await getArticleTags(id)()).data;
  const featuredNews = articleTags.filter((tag) => tag.type === "news");
  const tags = articleTags.filter((tag) => !tag.type.includes("news"));

  const clubCampaignIds = tags
    .filter((tag) => tag.type === "club")
    .map((tag) => getClubCampaignId(tag.id))
    .filter(Boolean) as number[];

  const campaigns = clubCampaignIds.length
    ? await getCampaignsByIds(clubCampaignIds)
    : await getCampaigns();

  const campaign = getRandomCampaign(campaigns.data);

  const gamesLeagueId = getGamesLeagueByCategoryId(article.categoriesId);

  const games = await getGames({
    leagueId: gamesLeagueId,
    clubId: getCampaignClubId(campaign?.id),
  });

  return {
    article,
    featuredNews,
    tags,
    games: games.data,
    campaign: campaign || null,
  };
};

export const getServerSideProps: GetServerSideProps<{
  articles?: ArticleWithTags[];
  ads?: API.Ad[];
  deviceType?: DeviceType;
}> = async (context) => {
  try {
    const { slug, id } = context.params as FixMeLater;
    const articleWithTags = await getArticleWithTags(id);
    console.log(articleWithTags);
    if (!articleWithTags?.article?.id) {
      context.res.statusCode = 404;

      return createServerSidePropsError(404);
    }

    const correctSlug = slugify(articleWithTags.article.title);
    const ads = await listByIds([
      AdsIds.panorama_1,
      AdsIds.mobile_1,
      AdsIds.dynamic_mobile,
      AdsIds.dynamic_desktop,
      AdsIds.tws_1,
      AdsIds.tws_2,
      AdsIds.desktop_vi,
      AdsIds.mobile_vi,
      AdsIds.casino_list,
      AdsIds.mobile_native,
      AdsIds.desktop_native,
      AdsIds.tws_insider,
      AdsIds.full_page_desktop,
      AdsIds.full_page_mobile,
      ...AdsIdsPanoramaCollection,
      ...AdsIdsMobileCollection,
    ]);

    if (correctSlug !== slug)
      return {
        redirect: {
          destination: `/nyheter/${correctSlug}/${id}`,
          permanent: true,
        },
      };

    return {
      props: {
        articles: [articleWithTags],
        deviceType: getDeviceType(context),
        ads: ads.data,
      },
    };
  } catch (error: FixMeLater) {
    const errorId = v4();
    const isUserError =
      error.response?.status >= 400 && error.response?.status < 500;
    const response = createServerSidePropsError(
      isUserError ? 404 : 500,
      isUserError ? undefined : errorId
    );

    if (!isUserError) logError(error, errorId);

    context.res.statusCode = isUserError ? 404 : 500;

    return response;
  }
};

const ArticlePage: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const adBlockDetected = useDetectAdBlock();
  const categoriesId = props.articles![0].article.categoriesId;
  const [articles, setArticles] = useState(props.articles!);

  const getArticles = async () => {
    // if (adBlockDetected) return;

    const newArticles = await getNewsList({
      after:
        articles.length === 1
          ? undefined
          : toMysqlDateString((last(articles) as ArticleWithTags).article.date),
      category: categoriesId,
      limit: 2,
    });
    const articlesWithTags = await Promise.all(
      newArticles.data.map((article) => {
        return getArticleWithTags(article.id, article);
      })
    );
    setArticles([
      ...articles,
      ...articlesWithTags.filter(
        (article) => article.article.id !== articles[0].article.id
      ),
    ]);
  };

  const canonicalUrl = useCanonicalUrl();

  const initialArticle = articles?.[0]?.article;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    ...(initialArticle?.title ? { headline: initialArticle.title } : {}),
    ...(initialArticle?.date
      ? { datePublished: `${initialArticle.date.replace(".000Z", "")}+01:00` }
      : {}),
    ...(initialArticle?.imageFull
      ? {
          image: `${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${initialArticle.imageFull}`,
        }
      : {}),
    ...(initialArticle?.writerName
      ? {
          author: {
            "@type": "Person",
            name: initialArticle.writerName,
          },
        }
      : {}),
    publisher: {
      "@type": "Organization",
      name: "Fotbolltransfers.com",
      logo: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/ft-round.svg`,
    },
  };

  return (
    <AdsContext.Provider value={props.ads!}>
      <DeviceTypeProvider value={props.deviceType!}>
        <Media lessThan="md">
          <Advertisement id={AdsIds.full_page_mobile} />
        </Media>
        <Media greaterThanOrEqual="md">
          <Advertisement id={AdsIds.full_page_desktop} />
        </Media>

        <Head>
          <link rel="icon" href="/favicon.ico" />
          <script async src="//www.instagram.com/embed.js"></script>
          <script async src="https://platform.twitter.com/widgets.js"></script>
          <link rel="canonical" href={canonicalUrl} />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          />
        </Head>

        <Container>
          <Navigation />
        </Container>

        <Media greaterThanOrEqual="md">
          <Container horizontalMargin>
            <PrimaryGrid
              top={
                <>
                  <Media greaterThanOrEqual="panorama">
                    <Advertisement id={AdsIds.panorama_1} />
                  </Media>
                  <Media lessThan="panorama">
                    <Advertisement id={AdsIds.mobile_1} />
                  </Media>
                </>
              }
              right={
                <StickySidebar>
                  <Advertisement id={AdsIds.tws_1} />
                  <div className={classNames("mb-5")} />
                  <Advertisement id={AdsIds.tws_2} />
                </StickySidebar>
              }
              main={
                <ArticleFeedDesktop
                  articles={articles}
                  loadMore={() => {
                    if (categoriesId !== 24) {
                      getArticles();
                    }
                  }}
                />
              }
            />
          </Container>
        </Media>

        <Media lessThan="md">
          <ArticleFeedMobile
            articles={articles}
            loadMore={() => {
              if (categoriesId !== 24) {
                getArticles();
              }
            }}
          />
        </Media>
      </DeviceTypeProvider>
    </AdsContext.Provider>
  );
};

export default ArticlePage;
